/**
 * LevelSubsystem - Level loading and management for the player runtime
 *
 * Responsible for:
 * - Loading serialized levels from the manifest
 * - Deserialising them into runtime Level objects
 * - Pre-loading textures referenced by sprites in the level
 * - Wiring the active level into RenderSubsystem and ScriptSubsystem
 */

import {
  Subsystem,
  type SubsystemConfig,
  SubsystemPriority,
  type Level,
  levelManager,
  LoadLevelMode,
  ResourceManager,
  resetTransformStorage,
  resetSpriteRendererStorage,
  deserializeLevel,
  type SerializedLevel,
  clearSpriteSync,
} from '@engine/core';

import type { AssetSubsystem } from './AssetSubsystem';
import type { RenderSubsystem } from './RenderSubsystem';
import type { PhysicsSubsystem } from './PhysicsSubsystem';
import type { AudioSubsystem } from './AudioSubsystem';
import type { VFXSubsystem } from './VFXSubsystem';
import type { ScriptSubsystem } from './ScriptSubsystem';
import type { InputSubsystem } from './InputSubsystem';
import { PlayerResourceBackends } from '../resources/PlayerResourceBackends';
import { PlayerLevelDependencyProvider } from '../resources/PlayerLevelDependencyProvider';

export interface LevelSubsystemOptions {
  entryLevel: string;
}

export class LevelSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'level',
    priority: SubsystemPriority.User + 10,
    optional: false,
    dependencies: ['assets', 'render', 'script', 'input'],
  };

  private readonly options: LevelSubsystemOptions;
  private _currentLevel: Level | null = null;

  // Refcounted asset ownership (background preload + unload).
  private _resourceManager: ResourceManager | null = null;
  private _depProvider: PlayerLevelDependencyProvider | null = null;
  /** Asset closure of the currently-active level (released when it's replaced/unloaded). */
  private _currentClosure: Set<string> | null = null;
  /** Levels preloaded (JSON + assets acquired) but not yet activated. */
  private _preloaded = new Map<string, { data: SerializedLevel; closure: Set<string> }>();

  constructor(options: LevelSubsystemOptions) {
    super();
    this.options = options;
  }

  get currentLevel(): Level | null {
    return this._currentLevel;
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  async onInitialize(): Promise<void> {
    console.log('[LevelSubsystem] Initialized');
  }

  async onReady(): Promise<void> {
    // Build the refcounted ResourceManager + dependency provider from sibling subsystems, and
    // register the loader so levelManager.loadLevel()/the scripting API route through here.
    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const render = this.getSubsystem<RenderSubsystem>('render');
    this._depProvider = new PlayerLevelDependencyProvider(assets);
    this._resourceManager = new ResourceManager(new PlayerResourceBackends(assets, render));
    levelManager.setLoaderHost({
      load: (guid, mode) => this.activateLevel(guid, mode),
      preload: (guid) => this.preloadLevel(guid),
      unload: (guid) => this.unloadLevel(guid),
      progress: (guid) => this.preloadProgress(guid),
    });

    // Load the entry level after all subsystems are ready
    const entryLevel = this._resolveEntryLevel();
    try {
      await this.loadLevel(entryLevel);
    } catch (err) {
      console.error(`[LevelSubsystem] Failed to load entry level '${entryLevel}':`, err);
    }
  }

  /**
   * Resolve which level to load on startup.
   *
   * The build writes the authoritative entry-level GUID into `manifest.entryLevel`.
   * In the browser the runner also passes it via `PlayerConfig.entryLevel` (read from
   * the generated HTML's `data-entry-level`). The native engine player has no HTML to
   * read, so its configured value is the placeholder `'main'` — whenever the configured
   * id isn't a real level in the manifest, fall back to `manifest.entryLevel`.
   */
  private _resolveEntryLevel(): string {
    const configured = this.options.entryLevel;
    const manifest = this.getSubsystem<AssetSubsystem>('assets').getManifest();
    if (manifest?.entryLevel && !manifest.assets.levels?.[configured]) {
      if (configured !== manifest.entryLevel) {
        console.warn(
          `[LevelSubsystem] Entry level '${configured}' not in manifest; using manifest.entryLevel '${manifest.entryLevel}'`,
        );
      }
      return manifest.entryLevel;
    }
    return configured;
  }

  onShutdown(): void {
    levelManager.setLoaderHost(null);
    if (this._currentLevel) {
      levelManager.unloadLevel(this._currentLevel);
      this._currentLevel = null;
    }
    if (this._currentClosure) {
      this._resourceManager?.release(this._currentClosure);
      this._currentClosure = null;
    }
    this._preloaded.clear();
    clearSpriteSync();
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /**
   * Load and switch to a new level (Single mode). Equivalent to preload + activate; kept for
   * back-compat with onReady/reloadLevel and PlayerApplication.loadLevel.
   */
  async loadLevel(levelId: string): Promise<Level> {
    await this.preloadLevel(levelId);
    return this.activateLevel(levelId, LoadLevelMode.Single);
  }

  /**
   * Preload a level in the background WITHOUT activating it: load the JSON, compute its asset
   * closure, and `acquire` (warm GPU/audio + incref) those assets. Safe to call while another
   * level is active and rendering — this is what makes the eventual swap seamless (shared assets
   * are already resident). Idempotent per level id.
   */
  async preloadLevel(levelId: string): Promise<void> {
    if (this._preloaded.has(levelId)) return;

    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const manifest = assets.getManifest();
    const levelPath = manifest?.assets.levels?.[levelId] ?? levelId;

    levelManager.events.emit('loadStart', { guid: levelId });
    try {
      const data = await assets.loadJson<SerializedLevel>(levelId, levelPath);
      const closure = (await this._depProvider?.getLevelClosure(levelId, data)) ?? new Set<string>();
      await this._resourceManager?.acquire(closure);
      this._preloaded.set(levelId, { data, closure });
      levelManager.events.emit('loaded', { guid: levelId });
    } catch (err) {
      levelManager.events.emit('error', { guid: levelId, error: err as Error });
      throw err;
    }
  }

  /** Progress of a preload (0..1). 1 once the level is preloaded, 0 otherwise. */
  preloadProgress(levelId: string): number {
    return this._preloaded.has(levelId) ? 1 : 0;
  }

  /**
   * Activate a (preloaded, or just-in-time loaded) level. Single mode tears down the previous
   * level and releases its assets AFTER acquiring the new one's — so assets shared by both never
   * unload-then-reload (seamless). Additive is gated until SoA segmentation lands.
   */
  async activateLevel(levelId: string, mode: LoadLevelMode = LoadLevelMode.Single): Promise<Level> {
    if (mode === LoadLevelMode.Additive) {
      throw new Error(
        '[LevelSubsystem] Additive level loading is not yet supported (requires SoA segmentation). Use Single.',
      );
    }

    if (!this._preloaded.has(levelId)) {
      await this.preloadLevel(levelId);
    }
    const entry = this._preloaded.get(levelId)!;
    this._preloaded.delete(levelId);

    const render = this.getSubsystem<RenderSubsystem>('render');
    const script = this.getSubsystem<ScriptSubsystem>('script');
    const physics = this.getSubsystem<PhysicsSubsystem>('physics');
    const audio = this.getSubsystem<AudioSubsystem>('audio');
    const vfx = this.getSubsystem<VFXSubsystem>('vfx');
    const input = this.getSubsystem<InputSubsystem>('input');

    const prevClosure = this._currentClosure;

    // --- Tear down previous level (Single mode: global SoA reset) ---
    if (this._currentLevel) {
      script?.destroyLevel();
      levelManager.unloadLevel(this._currentLevel);
      clearSpriteSync();
      resetTransformStorage();
      resetSpriteRendererStorage();
    }

    // --- Deserialize + activate ---
    const level = deserializeLevel(entry.data);
    levelManager.registerLevel(level);
    this._currentLevel = level;
    levelManager.activeLevel = level;

    render.setLevel(level);
    physics?.setLevel(level);
    audio?.setLevel(level).catch((err) =>
      console.warn('[LevelSubsystem] Audio setup failed:', err),
    );
    vfx?.setLevel(level).catch((err) =>
      console.warn('[LevelSubsystem] VFX setup failed:', err),
    );

    this.applyCameraSettings(level, render);

    // Wire input before the loop reads actions on the first frame.
    await input?.setupForLevel(level);

    // Initialise unified core.Script executors (TS behaviours + Redprint graphs).
    script?.initializeLevel(level);

    this._currentClosure = entry.closure;

    // Release the previous level's assets — shared ones survive via refcount (no flash).
    if (prevClosure) this._resourceManager?.release(prevClosure);

    levelManager.events.emit('activated', { guid: levelId, mode });
    console.log(`[LevelSubsystem] Activated level: ${levelId}`);
    return level;
  }

  /**
   * Unload a specific level (only the currently-active one is supported in Single mode): tear down
   * subsystems and release its assets.
   */
  async unloadLevel(levelId: string): Promise<void> {
    if (!this._currentLevel || this._currentLevel.guid !== levelId) {
      // Maybe it was only preloaded (not active) — release + drop it.
      const pre = this._preloaded.get(levelId);
      if (pre) {
        this._resourceManager?.release(pre.closure);
        this._preloaded.delete(levelId);
      }
      return;
    }

    const render = this.getSubsystem<RenderSubsystem>('render');
    const script = this.getSubsystem<ScriptSubsystem>('script');
    const physics = this.getSubsystem<PhysicsSubsystem>('physics');
    const audio = this.getSubsystem<AudioSubsystem>('audio');
    const vfx = this.getSubsystem<VFXSubsystem>('vfx');

    script?.destroyLevel();
    render?.setLevel(null);
    physics?.setLevel(null);
    await audio?.setLevel(null);
    await vfx?.setLevel(null);

    levelManager.unloadLevel(this._currentLevel);
    clearSpriteSync();
    resetTransformStorage();
    resetSpriteRendererStorage();

    if (this._currentClosure) this._resourceManager?.release(this._currentClosure);
    this._currentClosure = null;
    this._currentLevel = null;

    levelManager.events.emit('unloaded', { guid: levelId });
  }

  /** Reload the current level from scratch */
  async reloadLevel(): Promise<void> {
    if (this._currentLevel) {
      await this.loadLevel(this._resolveEntryLevel());
    }
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  /**
   * Read Camera2D component from the level and apply settings to
   * the render subsystem (background color, initial ortho size).
   */
  private applyCameraSettings(level: Level, render: RenderSubsystem): void {
    for (const go of level.getAllObjects()) {
      const camData = go.getComponentData('core.Camera2D');
      if (!camData) continue;

      // Background colour
      const bg = camData.backgroundColor;
      if (bg && typeof bg.r === 'number') {
        render.setBackgroundColor(bg.r, bg.g, bg.b);
      }

      // Initial camera state (position + ortho size → zoom)
      const orthoSize = camData.orthographicSize ?? 5;
      const t = go.transform;
      render.setCamera({
        x: t.position.x,
        y: t.position.y,
        zoom: 1 / Math.max(orthoSize, 0.001),
        rotation: 0,
      });

      break; // Use first Camera2D found
    }
  }
}
